// ImportExcelDialog.jsx
import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Alert,
  Form,
  Spinner,
  Tab,
  Tabs,
  Table,
  Badge,
  Row,
  Col,
  Dropdown,
  DropdownButton
} from 'react-bootstrap';
import { uploadExternalFile, startImport } from '../../api/photoProcessingApi';

const ImportExcelDialog = ({ show, onHide, experimentId, onImportSuccess }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState(null);
  const [importedFile, setImportedFile] = useState(null);
  const [selectedSheet, setSelectedSheet] = useState('');
  const [activeKey, setActiveKey] = useState('preview');
  const [currentPreview, setCurrentPreview] = useState(null);
  const [columnMapping, setColumnMapping] = useState({
    sample_info: null,
    class: [],
    data: []
  });
  const [autoDetected, setAutoDetected] = useState(false);
  const [detectionResults, setDetectionResults] = useState(null);
  const [lastDataColumnIndex, setLastDataColumnIndex] = useState(null);

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      const validExtensions = ['.xlsx', '.xls', '.csv'];
      const fileExtension = selectedFile.name.toLowerCase().substring(selectedFile.name.lastIndexOf('.'));

      if (!validExtensions.includes(fileExtension)) {
        setError('Please select an Excel file (.xlsx, .xls) or CSV file');
        return;
      }

      setFile(selectedFile);
      setError(null);
      setImportedFile(null);
      setCurrentPreview(null);
      setColumnMapping({ sample_info: null, class: [], data: [] });
      setAutoDetected(false);
      setLastDataColumnIndex(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const result = await uploadExternalFile(experimentId, file);
      setImportedFile(result);
      if (result.sheets && result.sheets.length > 0) {
        const initialSheet = result.selected_sheet || result.sheets[0];
        setSelectedSheet(initialSheet);
        updatePreviewForSheet(initialSheet, result);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const updatePreviewForSheet = (sheetName, fileData = importedFile) => {
    if (!fileData || !fileData.sheet_data || !fileData.sheet_data[sheetName]) {
      setCurrentPreview(null);
      return;
    }

    const sheetInfo = fileData.sheet_data[sheetName];

    // Check if we have full_preview data in sheet_data
    if (sheetInfo.full_preview) {
      setCurrentPreview(sheetInfo.full_preview);
    } else if (sheetInfo.preview) {
      // Convert compact preview to full preview format
      const headers = sheetInfo.columns || [];
      const previewRows = sheetInfo.preview || [];

      setCurrentPreview({
        rows: previewRows.length,
        columns: headers.length,
        data: previewRows,
        headers: headers,
        sheet: sheetName
      });
    } else if (sheetName === fileData.preview_data?.sheet) {
      setCurrentPreview(fileData.preview_data);
    } else {
      setCurrentPreview(null);
    }
  };

  useEffect(() => {
    if (importedFile && selectedSheet) {
      updatePreviewForSheet(selectedSheet);
      // Reset column mapping when sheet changes
      setColumnMapping({ sample_info: null, class: [], data: [] });
      setAutoDetected(false);
      setLastDataColumnIndex(null);
    }
  }, [selectedSheet, importedFile]);

  const handleSheetChange = (sheetName) => {
    setSelectedSheet(sheetName);
  };

  // Function to auto-detect columns based on headers
  const autoDetectColumns = () => {
    if (!currentPreview || !currentPreview.headers) {
      setError('No preview data available');
      return;
    }

    const headers = currentPreview.headers;
    const newMapping = {
      sample_info: null,
      class: [],
      data: []
    };

    // Simple detection logic
    headers.forEach((header, index) => {
      const headerStr = String(header).toLowerCase().trim();

      // Check if it's a sample name column
      if (headerStr.includes('sample') || headerStr.includes('name') || headerStr === '') {
        if (newMapping.sample_info === null) {
          newMapping.sample_info = index;
        } else {
          newMapping.class.push(index);
        }
      }
      // Check if it's a data column (ends with R, G, B, Avg or contains image extensions)
      else if (headerStr.endsWith(' r') || headerStr.endsWith(' g') || headerStr.endsWith(' b') ||
               headerStr.endsWith(' avg') || headerStr.includes('.jpg') || headerStr.includes('.jpeg') ||
               headerStr.includes('.png') || headerStr.includes('_r') || headerStr.includes('_g') ||
               headerStr.includes('_b') || headerStr.includes('_intensity')) {
        newMapping.data.push(index);
      }
      // Default to classification column
      else {
        newMapping.class.push(index);
      }
    });

    // If no sample column detected, use first column
    if (newMapping.sample_info === null && headers.length > 0) {
      newMapping.sample_info = 0;
      // Remove from class if it was added there
      const classIndex = newMapping.class.indexOf(0);
      if (classIndex !== -1) {
        newMapping.class.splice(classIndex, 1);
      }
    }

    setColumnMapping(newMapping);
    setAutoDetected(true);
    setDetectionResults({
      sample_column: headers[newMapping.sample_info] || 'Column ' + (newMapping.sample_info + 1),
      class_columns: newMapping.class.map(idx => headers[idx] || 'Column ' + (idx + 1)),
      data_columns: newMapping.data.map(idx => headers[idx] || 'Column ' + (idx + 1))
    });
  };

  // Function to handle column type selection
  const handleColumnTypeSelect = (columnIndex, type) => {
    const newMapping = { ...columnMapping };

    // Remove from all types first
    if (newMapping.sample_info === columnIndex) {
      newMapping.sample_info = null;
    }
    newMapping.class = newMapping.class.filter(idx => idx !== columnIndex);
    newMapping.data = newMapping.data.filter(idx => idx !== columnIndex);

    // Add to selected type
    if (type === 'sample_info') {
      newMapping.sample_info = columnIndex;
    } else if (type === 'class') {
      newMapping.class.push(columnIndex);
    } else if (type === 'data') {
      newMapping.data.push(columnIndex);
    }

    setColumnMapping(newMapping);
    setAutoDetected(false);
    setLastDataColumnIndex(null); // Reset last data column when manually selecting
  };

  // Function to set all columns after selected column as data columns
  const setAllAfterAsData = (startIndex) => {
    if (!currentPreview || !currentPreview.headers || startIndex >= currentPreview.headers.length) {
      return;
    }

    const newMapping = { ...columnMapping };
    const totalColumns = currentPreview.headers.length;

    // Mark all columns from startIndex to end as data
    for (let i = startIndex; i < totalColumns; i++) {
      // Remove from other types first
      if (newMapping.sample_info === i) {
        newMapping.sample_info = null;
      }
      newMapping.class = newMapping.class.filter(idx => idx !== i);
      newMapping.data = newMapping.data.filter(idx => idx !== i);

      // Add to data
      newMapping.data.push(i);
    }

    setColumnMapping(newMapping);
    setLastDataColumnIndex(startIndex);
    setAutoDetected(false);
  };

  // Function to get column type badge color
  const getColumnTypeBadge = (columnIndex) => {
    if (columnMapping.sample_info === columnIndex) return 'primary';
    if (columnMapping.class.includes(columnIndex)) return 'info';
    if (columnMapping.data.includes(columnIndex)) return 'success';
    return 'secondary';
  };

  // Function to get column type text
  const getColumnTypeText = (columnIndex) => {
    if (columnMapping.sample_info === columnIndex) return 'Sample Name';
    if (columnMapping.class.includes(columnIndex)) return 'Classification';
    if (columnMapping.data.includes(columnIndex)) return 'Data';
    return 'Undefined';
  };

  // Function to clear all column mappings
  const clearAllMappings = () => {
    setColumnMapping({ sample_info: null, class: [], data: [] });
    setLastDataColumnIndex(null);
    setAutoDetected(false);
  };

  const handleImport = async () => {
    if (!importedFile) {
      setError('No file uploaded');
      return;
    }

    // Validate column mapping
    if (columnMapping.sample_info === null) {
      setError('Please select a sample name column or use auto-detect');
      return;
    }

    setImporting(true);
    setError(null);

    try {
      // Prepare column mapping data
      const columnMappingData = {
        sample_info: columnMapping.sample_info,
        class: columnMapping.class,
        data: columnMapping.data
      };

      await startImport(experimentId, importedFile.id, selectedSheet, columnMappingData);
      onImportSuccess?.();
      onHide();
    } catch (err) {
      setError(err.response?.data?.error || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const renderColumnMappingControls = () => {
    if (!currentPreview || !currentPreview.headers) return null;

    const totalColumns = currentPreview.headers.length;

    return (
      <div className="mt-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6>Column Mapping</h6>
          <div className="d-flex gap-2">
            <Button
              variant="outline-primary"
              size="sm"
              onClick={autoDetectColumns}
              disabled={!currentPreview}
            >
              <i className="bi bi-magic me-1"></i>
              Auto-detect
            </Button>
            <Button
              variant="outline-danger"
              size="sm"
              onClick={clearAllMappings}
            >
              <i className="bi bi-x-circle me-1"></i>
              Clear All
            </Button>
          </div>
        </div>

        {autoDetected && detectionResults && (
          <Alert variant="info" className="mb-3">
            <strong>Auto-detection results:</strong>
            <div className="mt-2">
              <Badge bg="primary" className="me-2">Sample: {detectionResults.sample_column}</Badge>
              <Badge bg="info" className="me-2">Classes: {detectionResults.class_columns.length}</Badge>
              <Badge bg="success">Data: {detectionResults.data_columns.length}</Badge>
            </div>
          </Alert>
        )}

        {lastDataColumnIndex !== null && (
          <Alert variant="warning" className="mb-3">
            <i className="bi bi-info-circle me-2"></i>
            All columns from <strong>Column {lastDataColumnIndex + 1}</strong> to <strong>Column {totalColumns}</strong> are set as Data columns.
          </Alert>
        )}

        <div className="mb-3">
          <p className="text-muted mb-2">
            <strong>Instructions:</strong>
          </p>
          <ul className="text-muted small mb-2">
            <li>Click on column headers to assign types (cycles through: Sample → Class → Data)</li>
            <li>Use "Set All After as Data" to quickly mark all columns from a starting point as data</li>
            <li>Use "Auto-detect" for automatic column detection</li>
          </ul>

          <div className="d-flex align-items-center gap-2 mb-2">
            <span className="text-muted">Quick Actions:</span>
            <DropdownButton
              variant="outline-success"
              title="Set All After as Data"
              size="sm"
            >
              {currentPreview.headers.map((header, idx) => (
                <Dropdown.Item
                  key={idx}
                  onClick={() => setAllAfterAsData(idx)}
                  active={lastDataColumnIndex === idx}
                >
                  From Column {idx + 1}: {header || `Column ${idx + 1}`}
                </Dropdown.Item>
              ))}
            </DropdownButton>
          </div>

          <div className="d-flex gap-2 mt-2">
            <Badge bg="primary" className="me-2">Sample Name</Badge>
            <Badge bg="info" className="me-2">Classification</Badge>
            <Badge bg="success" className="me-2">Data</Badge>
            <Badge bg="secondary">Undefined</Badge>
          </div>
        </div>
      </div>
    );
  };

  const renderFileInfo = () => {
    if (!importedFile) return null;

    const sheetInfo = importedFile.sheet_data?.[selectedSheet];

    return (
      <div className="mt-3">
        <div className="d-flex align-items-center mb-3">
          <i className="bi bi-file-earmark-text me-2 text-primary fs-4"></i>
          <h5 className="mb-0">{importedFile.original_filename}</h5>
          <Badge bg="secondary" className="ms-2">
            {(importedFile.file_size_mb || 0).toFixed(2)} MB
          </Badge>
        </div>

        <Tabs
          activeKey={activeKey}
          onSelect={(k) => setActiveKey(k)}
          className="mb-3"
        >
          <Tab eventKey="sheets" title={<><i className="bi bi-table me-1"></i> Sheets</>}>
            <div className="mt-3">
              <Form.Group className="mb-3">
                <Form.Label>Select Sheet</Form.Label>
                <Form.Select
                  value={selectedSheet}
                  onChange={(e) => handleSheetChange(e.target.value)}
                  size="sm"
                >
                  {importedFile.sheets?.map((sheet) => (
                    <option key={sheet} value={sheet}>
                      {sheet}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              {sheetInfo && (
                <div className="mt-3">
                  <h6>Columns in {selectedSheet}:</h6>
                  <div className="d-flex flex-wrap gap-1 mb-2">
                    {sheetInfo.columns?.map((col, idx) => (
                      <Badge key={idx} bg={getColumnTypeBadge(idx)} className="border">
                        {col || `Column ${idx + 1}`}
                      </Badge>
                    ))}
                  </div>
                  <small className="text-muted">
                    {sheetInfo.row_count || 0} rows
                    {sheetInfo.encoding && ` • Encoding: ${sheetInfo.encoding}`}
                  </small>
                </div>
              )}
            </div>
          </Tab>

          <Tab eventKey="preview" title={<><i className="bi bi-eye me-1"></i> Preview</>}>
            {renderColumnMappingControls()}

            {currentPreview ? (
              <div className="mt-3">
                <h6>
                  Preview of {currentPreview.sheet}
                  <small className="text-muted ms-2">
                    ({currentPreview.rows} rows, {currentPreview.columns} columns shown)
                  </small>
                </h6>

                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  <Table striped bordered hover size="sm">
                    <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa' }}>
                      <tr>
                        {currentPreview.headers?.map((header, idx) => (
                          <th
                            key={idx}
                            className="fw-bold"
                            style={{
                              cursor: 'pointer',
                              backgroundColor:
                                columnMapping.sample_info === idx ? '#cfe2ff' :
                                columnMapping.class.includes(idx) ? '#d1ecf1' :
                                columnMapping.data.includes(idx) ? '#d4edda' : 'white',
                              position: 'relative'
                            }}
                            onClick={(e) => {
                              // Check if right-click or Ctrl+click for quick action
                              if (e.ctrlKey || e.metaKey) {
                                // Ctrl/Cmd+click: Set all after as data
                                setAllAfterAsData(idx);
                              } else {
                                // Regular click: Cycle through column types
                                const currentType =
                                  columnMapping.sample_info === idx ? 'sample_info' :
                                  columnMapping.class.includes(idx) ? 'class' :
                                  columnMapping.data.includes(idx) ? 'data' : null;

                                const nextType =
                                  currentType === 'sample_info' ? 'class' :
                                  currentType === 'class' ? 'data' :
                                  currentType === 'data' ? 'sample_info' : 'sample_info';

                                handleColumnTypeSelect(idx, nextType);
                              }
                            }}
                          >
                            <div className="d-flex justify-content-between align-items-center">
                              <span>{header || `Column ${idx + 1}`}</span>
                              <div className="d-flex align-items-center gap-1">
                                {lastDataColumnIndex === idx && (
                                  <i className="bi bi-arrow-right text-warning" title="Data columns start here"></i>
                                )}
                                <Badge
                                  bg={getColumnTypeBadge(idx)}
                                  size="sm"
                                  className="ms-1"
                                >
                                  {getColumnTypeText(idx).charAt(0)}
                                </Badge>
                              </div>
                            </div>
                            <div className="position-absolute top-0 end-0 me-1">
                              <small className="text-muted" style={{ fontSize: '10px' }}>
                                {idx + 1}
                              </small>
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {currentPreview.data?.map((row, rowIdx) => (
                        <tr key={rowIdx}>
                          {currentPreview.headers?.map((header, colIdx) => (
                            <td key={colIdx}>
                              {row[header] !== null && row[header] !== undefined ?
                                (typeof row[header] === 'number' ?
                                  row[header].toFixed(4) :
                                  String(row[header])
                                ) : ''}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                {/* Column Mapping Summary */}
                <div className="mt-3 p-3 bg-light rounded">
                  <h6>Column Mapping Summary:</h6>
                  <Row>
                    <Col md={4}>
                      <strong className="text-primary">Sample Name:</strong>
                      <div className="mt-1">
                        {columnMapping.sample_info !== null ? (
                          <Badge bg="primary">
                            {currentPreview.headers[columnMapping.sample_info] || `Column ${columnMapping.sample_info + 1}`}
                          </Badge>
                        ) : (
                          <span className="text-danger">Not selected</span>
                        )}
                      </div>
                    </Col>
                    <Col md={4}>
                      <strong className="text-info">Classification ({columnMapping.class.length}):</strong>
                      <div className="mt-1 d-flex flex-wrap gap-1">
                        {columnMapping.class.map(idx => (
                          <Badge key={idx} bg="info">
                            {currentPreview.headers[idx] || `Column ${idx + 1}`}
                          </Badge>
                        ))}
                        {columnMapping.class.length === 0 && (
                          <span className="text-muted">None</span>
                        )}
                      </div>
                    </Col>
                    <Col md={4}>
                      <strong className="text-success">Data ({columnMapping.data.length}):</strong>
                      <div className="mt-1 d-flex flex-wrap gap-1">
                        {columnMapping.data.map(idx => (
                          <Badge key={idx} bg="success">
                            {currentPreview.headers[idx] || `Column ${idx + 1}`}
                          </Badge>
                        ))}
                        {columnMapping.data.length === 0 && (
                          <span className="text-muted">None</span>
                        )}
                      </div>
                    </Col>
                  </Row>
                </div>

                {/* Quick Tips */}
                <div className="mt-3">
                  <Alert variant="light" className="mb-0">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <i className="bi bi-lightbulb me-2 text-warning"></i>
                        <strong>Quick Tips:</strong>
                      </div>
                      <small className="text-muted">
                        <i className="bi bi-mouse me-1"></i>
                        Click: Cycle type •
                        <i className="bi bi-command ms-2 me-1"></i>
                        Ctrl+Click: Set all after as Data
                      </small>
                    </div>
                  </Alert>
                </div>
              </div>
            ) : (
              <div className="text-center py-4">
                <i className="bi bi-eye-slash text-muted" style={{ fontSize: '2rem' }}></i>
                <p className="text-muted mt-2">No preview available for this sheet</p>
              </div>
            )}
          </Tab>

          <Tab eventKey="info" title={<><i className="bi bi-info-circle me-1"></i> Info</>}>
            <div className="mt-3">
              <h6>File Information</h6>
              <Table borderless size="sm">
                <tbody>
                  <tr>
                    <td className="fw-bold" width="40%">Total Sheets</td>
                    <td>{importedFile.sheets?.length || 0}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Selected Sheet</td>
                    <td>{selectedSheet}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Current Sheet Columns</td>
                    <td>{sheetInfo?.columns?.length || 0}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Current Sheet Rows</td>
                    <td>{sheetInfo?.row_count || 0}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">File Type</td>
                    <td>{importedFile.column_info?.file_type || 'Unknown'}</td>
                  </tr>
                  {importedFile.column_info?.encoding && (
                    <tr>
                      <td className="fw-bold">Encoding</td>
                      <td>{importedFile.column_info.encoding}</td>
                    </tr>
                  )}
                  <tr>
                    <td className="fw-bold">Status</td>
                    <td>
                      <Badge
                        bg={
                          importedFile.status === 'completed' ? 'success' :
                          importedFile.status === 'failed' ? 'danger' :
                          importedFile.status === 'running' ? 'warning' : 'secondary'
                        }
                      >
                        {importedFile.status}
                      </Badge>
                    </td>
                  </tr>
                </tbody>
              </Table>
            </div>
          </Tab>
        </Tabs>
      </div>
    );
  };

  const renderUploadArea = () => (
    <div
      className="border-2 border-dashed rounded-3 p-5 text-center"
      style={{
        borderColor: '#dee2e6',
        cursor: 'pointer',
        borderStyle: 'dashed'
      }}
      onClick={() => document.getElementById('file-input').click()}
    >
      <input
        id="file-input"
        type="file"
        accept=".xlsx,.xls,.csv"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      <i className="bi bi-cloud-arrow-up text-muted mb-3" style={{ fontSize: '3rem' }}></i>
      <h5 className="mb-2">Drag & drop or click to upload</h5>
      <p className="text-muted mb-0">
        Excel files (.xlsx, .xls) or CSV files
      </p>

      {file && (
        <div className="mt-3 p-3 bg-light rounded">
          <p className="mb-0">
            Selected: <strong>{file.name}</strong> ({((file.size / 1024) / 1024).toFixed(2)} MB)
          </p>
        </div>
      )}
    </div>
  );

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      className="import-excel-dialog"
    >
      <Modal.Header closeButton className="border-bottom">
        <Modal.Title>
          <i className="bi bi-file-earmark-arrow-down me-2"></i>
          Import Excel Data
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-0">
        <div style={{
          maxHeight: 'calc(100vh - 210px)',
          overflowY: 'auto',
          padding: '1rem'
        }}>
          {error && (
            <Alert variant="danger" className="mb-3" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {!importedFile ? renderUploadArea() : renderFileInfo()}
        </div>
      </Modal.Body>

      <Modal.Footer className="border-top bg-light">
        <Button
          variant="secondary"
          onClick={onHide}
          disabled={uploading || importing}
        >
          Cancel
        </Button>

        {!importedFile ? (
          <Button
            variant="primary"
            onClick={handleUpload}
            disabled={!file || uploading}
          >
            {uploading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Uploading...
              </>
            ) : (
              <>
                <i className="bi bi-cloud-arrow-up me-2"></i>
                Upload & Preview
              </>
            )}
          </Button>
        ) : (
          <>
            <Button
              variant="success"
              onClick={handleImport}
              disabled={importing || columnMapping.sample_info === null}
            >
              {importing ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Importing...
                </>
              ) : (
                <>
                  <i className="bi bi-arrow-right-circle me-2"></i>
                  Start Import
                </>
              )}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ImportExcelDialog;